import { signInWithIdTokenApiAuthIdTokenPost } from '../client/sdk.gen';
import type { AuthResponse } from '../client/types.gen';

/**
 * Google credential response from Google Identity Services
 * @see https://developers.google.com/identity/gsi/web/reference/js-reference#CredentialResponse
 */
export interface GoogleCredentialResponse {
  /** The JWT credential string */
  credential: string;
  /** How the credential was selected */
  select_by?: 'auto' | 'user' | 'user_1tap' | 'user_2tap' | 'btn' | 'btn_confirm' | 'brn_add_session' | 'btn_confirm_add_session';
  /** The client ID */
  clientId?: string;
  /** Raw nonce that pairs with the hashed nonce embedded in the JWT */
  nonce?: string;
}

/**
 * Handle Google Sign-In response
 * @param response - The credential response from Google Identity Services
 * @returns The authentication response from the backend
 */
export async function handleSignInWithGoogle(
  response: GoogleCredentialResponse
): Promise<AuthResponse> {
  const { data } = await signInWithIdTokenApiAuthIdTokenPost({
    body: {
      provider: 'google',
      token: response.credential,
      client_id: response.clientId ?? null,
      nonce: response.nonce ?? null,
    },
  });

  if (!data) {
    throw new Error('Authentication failed: No response data received');
  }

  return data;
}

/**
 * Disable Google One Tap automatic sign-in
 * Call this after user explicitly signs out to prevent automatic re-sign-in
 * @see https://developers.google.com/identity/gsi/web/guides/automatic-sign-in-sign-out
 */
export function disableGoogleAutoSelect(): void {
  if (typeof window !== 'undefined' && window.google?.accounts?.id) {
    window.google.accounts.id.disableAutoSelect();
  }
}
